import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// Sanitize: strip trailing dots (DNS root) and trailing slashes from env var
export const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\.+\/*$/, '').replace(/\/+$/, '');
export const API_HOST = API_BASE_URL.replace('/api', '');

/**
 * Get the full URL for a media file.
 * Handles both local development (relative paths) and production (Spaces absolute URLs).
 * @param url - The media URL from the API (relative like /media/... or absolute like https://...)
 * @returns The full URL to use in img src
 */
export const getMediaUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  // If already absolute URL (Spaces, CDN, etc.), use as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // Relative path - prepend API host
  return `${API_HOST}${url}`;
};

// Helper to clear all auth data (user data only, cookies handled by backend)
const clearAllAuthData = () => {
  localStorage.removeItem('user');
  localStorage.removeItem('rbf_user_data');
};

// Client-side request ceiling. There was none, so axios waited on the browser's
// own (minutes), and a request the server had already abandoned still looked
// alive to the UI.
//
// This MUST sit ABOVE the server's worst case, never below it. Giving up early
// on work the backend is still doing is how you get a "failed" toast on an
// action that succeeded — exactly the Sentry 31ea0afe invoice_review case.
// Server ceiling is gunicorn --timeout 120 (backend/start.sh); 150s leaves
// headroom for TLS + DO's edge without ever being the first to quit.
const REQUEST_TIMEOUT_MS = 150_000;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests
});

// Response interceptor - handle 401 errors by redirecting to login
// ONLY redirect if we're on admin/team routes (not portal, club, etc.)
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Only handle 401 errors that haven't been retried
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Skip redirect for auth-check endpoints — 401 is expected (means "not logged in")
    const url = originalRequest.url || '';
    if (url.includes('/me/') || url.includes('/my-locations/') || url.includes('/auth/login') || url.includes('/token/')) {
      return Promise.reject(error);
    }

    const pathname = window.location.pathname;

    // Don't redirect if already on any login page - this prevents login loops
    if (pathname.includes('/login')) {
      return Promise.reject(error);
    }

    // Redirect store routes to store login
    if (pathname.startsWith('/store')) {
      console.log('[API] 401 on store route, redirecting to /store/login');
      clearAllAuthData();
      window.location.href = '/store/login';
      return Promise.reject(error);
    }

    // Redirect mill routes to mill login
    if (pathname.startsWith('/mill')) {
      console.log('[API] 401 on mill route, redirecting to /mill/login');
      clearAllAuthData();
      window.location.href = '/mill/login';
      return Promise.reject(error);
    }

    // Don't redirect from portal, club, or bridge-club routes - those have their own handlers
    if (pathname.startsWith('/portal') || pathname.startsWith('/club') ||
        pathname.startsWith('/bridge-club')) {
      console.log('[API] 401 on non-admin route, not redirecting');
      return Promise.reject(error);
    }

    // Only redirect to /login from admin routes (/, /dashboard, /orders, etc.)
    console.log('[API] 401 unauthorized on admin route, redirecting to login');
    clearAllAuthData();
    window.location.href = '/login';
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (username: string, password: string) => {
    const response = await axios.post(`${API_BASE_URL}/auth/login/`, {
      username,
      password,
    }, { withCredentials: true }); // Important: send cookies with login request
    return response.data;
  },
  logout: async () => {
    try {
      await api.post('/auth/logout/');
    } catch (e) {
      // Ignore errors
    }
    clearAllAuthData();
  },
  me: async () => {
    const response = await api.get('/auth/me/');
    return response.data;
  },
};

export const usersAPI = {
  getAll: () => api.get('/users/'),
  getById: (id: number) => api.get(`/users/${id}/`),
  getEmployees: () => api.get('/users/employees/'),
  create: (data: any) => api.post('/users/', data),
  update: (id: number, data: any) => api.put(`/users/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/users/${id}/`, data),
  delete: (id: number) => api.delete(`/users/${id}/`),
  resetPassword: (id: number, password: string) => api.post(`/users/${id}/reset_password/`, { password }),
  toggleActive: (id: number) => api.post(`/users/${id}/toggle_active/`),
};

export interface CustomersQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  quick_filter?: 'active' | 'inactive' | 'wholesale' | 'retail';
  is_active?: boolean;
  customer_type?: string;
  paginate?: boolean;
}

export const customersAPI = {
  // Paginated list with filters
  getAll: (params?: CustomersQueryParams) => {
    const queryParams = new URLSearchParams();
    if (params) {
      if (params.page) queryParams.append('page', String(params.page));
      if (params.page_size) queryParams.append('page_size', String(params.page_size));
      if (params.search) queryParams.append('search', params.search);
      if (params.quick_filter) queryParams.append('quick_filter', params.quick_filter);
      if (params.is_active !== undefined) queryParams.append('is_active', String(params.is_active));
      if (params.customer_type) queryParams.append('customer_type', params.customer_type);
      if (params.paginate === false) queryParams.append('paginate', 'false');
    }
    const queryString = queryParams.toString();
    return api.get(`/customers/${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id: number) => api.get(`/customers/${id}/`),
  create: (data: any) => api.post('/customers/', data),
  update: (id: number, data: any) => api.put(`/customers/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/customers/${id}/`, data),
  delete: (id: number) => api.delete(`/customers/${id}/`),
  deactivate: (id: number) => api.post(`/customers/${id}/deactivate/`),
  reactivate: (id: number) => api.post(`/customers/${id}/reactivate/`),
  upgradeClassification: (id: number, customerType: string) =>
    api.post(`/customers/${id}/upgrade_classification/`, { customer_type: customerType }),
  // Delivery info / shipping preferences
  getDeliveryInfo: (customerId: number) => api.get(`/customers/${customerId}/delivery_info/`),
  updateDeliveryInfo: (customerId: number, data: any) => api.patch(`/customers/${customerId}/delivery_info/`, data),
  getValidDeliveryDates: (customerId: number) => api.get(`/shipping/customer/${customerId}/valid-dates/`),
  getShippingProfile: (customerId: number) => api.get(`/customers/${customerId}/shipping-profile/`),
  exportCSV: () => api.get('/customers/export/', { responseType: 'blob' }),
  exportRingCentral: () => api.get('/customers/export/ringcentral/', { responseType: 'blob' }),
};

export const suppliersAPI = {
  getAll: () => api.get('/suppliers/'),
  getById: (id: number) => api.get(`/suppliers/${id}/`),
  create: (data: any) => api.post('/suppliers/', data),
  update: (id: number, data: any) => api.put(`/suppliers/${id}/`, data),
  delete: (id: number) => api.delete(`/suppliers/${id}/`),
};

export const rawMaterialsAPI = {
  getAll: () => api.get('/raw-materials/'),
  getById: (id: number) => api.get(`/raw-materials/${id}/`),
  create: (data: any) => api.post('/raw-materials/', data),
  update: (id: number, data: any) => api.put(`/raw-materials/${id}/`, data),
  // PATCH a subset of fields (e.g. only nutrient fields) without touching name/cost/category/stock.
  partialUpdate: (id: number, data: any) => api.patch(`/raw-materials/${id}/`, data),
  delete: (id: number) => api.delete(`/raw-materials/${id}/`),
};

export const feedFormulasAPI = {
  getAll: (params?: { mill_only?: boolean; is_active?: boolean }) => api.get('/feed-formulas/', { params }),
  getById: (id: number) => api.get(`/feed-formulas/${id}/`),
  create: (data: any) => api.post('/feed-formulas/', data),
  update: (id: number, data: any) => api.put(`/feed-formulas/${id}/`, data),
  partialUpdate: (id: number, data: any) => api.patch(`/feed-formulas/${id}/`, data),
  delete: (id: number) => api.delete(`/feed-formulas/${id}/`),
  getFilterOptions: (params?: { feed_grade?: string; animal_type?: string }) =>
    api.get('/feed-formulas/filter_options/', { params }),
  // Versioned editor: Save creates a DRAFT version; Activate makes it live (supersedes prior).
  saveDraft: (id: number, data: { ingredients: any[]; notes?: string }) =>
    api.post(`/feed-formulas/${id}/save_draft/`, data),
  activateVersion: (id: number, versionId: number) =>
    api.post(`/feed-formulas/${id}/activate_version/`, { version_id: versionId }),
  discardDraft: (id: number, versionId: number) =>
    api.post(`/feed-formulas/${id}/discard_draft/`, { version_id: versionId }),
};

export const productionBatchesAPI = {
  getAll: () => api.get('/production-batches/'),
  getById: (id: number) => api.get(`/production-batches/${id}/`),
  create: (data: any) => api.post('/production-batches/', data),
  update: (id: number, data: any) => api.put(`/production-batches/${id}/`, data),
  delete: (id: number) => api.delete(`/production-batches/${id}/`),
};

export const inventoryAPI = {
  getAll: () => api.get('/inventory/'),
  getById: (id: number) => api.get(`/inventory/${id}/`),
  getLowStock: () => api.get('/inventory/low_stock/'),
  update: (id: number, data: any) => api.put(`/inventory/${id}/`, data),
};

export interface OrdersQueryParams {
  tab?: 'needs_attention' | 'in_progress' | 'shipped' | 'delivered' | 'drafts' | 'all';
  page?: number;
  page_size?: number;
  status?: string;
  customer_search?: string;
  delivery_after?: string;
  delivery_before?: string;
  include_archived?: boolean;
  paginate?: boolean;
  ordering?: string;
}

export const salesOrdersAPI = {
  // Paginated list with filters
  getAll: (params?: OrdersQueryParams) => {
    const queryParams = new URLSearchParams();
    if (params) {
      if (params.tab) queryParams.append('tab', params.tab);
      if (params.page) queryParams.append('page', String(params.page));
      if (params.page_size) queryParams.append('page_size', String(params.page_size));
      if (params.status) queryParams.append('status', params.status);
      if (params.customer_search) queryParams.append('customer_search', params.customer_search);
      if (params.delivery_after) queryParams.append('delivery_after', params.delivery_after);
      if (params.delivery_before) queryParams.append('delivery_before', params.delivery_before);
      if (params.include_archived) queryParams.append('include_archived', 'true');
      if (params.paginate === false) queryParams.append('paginate', 'false');
      if (params.ordering) queryParams.append('ordering', params.ordering);
    }
    const queryString = queryParams.toString();
    return api.get(`/sales-orders/${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id: number) => api.get(`/sales-orders/${id}/`),
  create: (data: any) => api.post('/sales-orders/', data),
  update: (id: number, data: any) => api.put(`/sales-orders/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/sales-orders/${id}/`, data),
  delete: (id: number) => api.delete(`/sales-orders/${id}/`),
  calculatePrice: (data: { customer_id: number; items: any[] }) =>
    api.post('/sales-orders/calculate_price/', data),
  checkMaterials: (data: { formula_id: number; quantity_lbs: number }) =>
    api.post('/sales-orders/check_materials/', data),
  confirmOrder: (id: number) => api.post(`/sales-orders/${id}/confirm_order/`),
  updateStatus: (id: number, data: { status: string; note?: string; tracking_number?: string; carrier?: string; send_email?: boolean }) =>
    api.post(`/sales-orders/${id}/update-status/`, data),
  validateDelivery: (data: { customer_id: number; delivery_date: string; shipping_method: string }) =>
    api.post('/sales-orders/validate_delivery/', data),
  // Bulk actions
  bulkConfirm: (orderIds: number[]) => api.post('/sales-orders/bulk-confirm/', { order_ids: orderIds }),
  bulkDelete: (orderIds: number[]) => api.post('/sales-orders/bulk-delete/', { order_ids: orderIds }),
  bulkMarkDelivered: (orderIds: number[], sendEmail: boolean) =>
    api.post('/sales-orders/bulk-mark-delivered/', { order_ids: orderIds, send_email: sendEmail }),
  // Tab counts for badges
  getTabCounts: () => api.get('/sales-orders/tab-counts/'),
  // This week's orders for dashboard
  getThisWeek: () => api.get('/sales-orders/this-week/'),
};

export const purchaseOrdersAPI = {
  getAll: () => api.get('/purchase-orders/'),
  getById: (id: number) => api.get(`/purchase-orders/${id}/`),
  create: (data: any) => api.post('/purchase-orders/', data),
  update: (id: number, data: any) => api.put(`/purchase-orders/${id}/`, data),
  delete: (id: number) => api.delete(`/purchase-orders/${id}/`),
};

// Catalogue items offered in the order form's Add-ons section: bulk
// ingredients, canola oil, grit. `?addons=true` already implies active.
export const addonProductsAPI = {
  getAll: () => api.get('/products/?addons=true'),
};

export const quotesAPI = {
  getAll: () => api.get('/quotes/'),
  getById: (id: number) => api.get(`/quotes/${id}/`),
  getByCustomer: (customerId: number) => api.get(`/quotes/by_customer/?customer_id=${customerId}`),
  create: (data: any) => api.post('/quotes/', data),
  update: (id: number, data: any) => api.put(`/quotes/${id}/`, data),
  delete: (id: number) => api.delete(`/quotes/${id}/`),
  convertToOrder: (id: number, data?: { delivery_date?: string }) =>
    api.post(`/quotes/${id}/convert_to_order/`, data || {}),
  calculatePrice: (data: { customer_id: number; items: any[] }) =>
    api.post('/quotes/calculate_price/', data),
};

export const dashboardAPI = {
  getSummary: () => api.get('/dashboard/summary/'),
};

// Admin Tax Documents API
export const adminTaxDocumentsAPI = {
  getAll: (params?: { status?: string; customer_id?: number }) =>
    api.get('/admin/tax-documents/', { params }),
  getById: (id: number) => api.get(`/admin/tax-documents/${id}/`),
  getSummary: () => api.get('/admin/tax-documents/summary/'),
  approve: (id: number, notes?: string) =>
    api.post(`/admin/tax-documents/${id}/approve/`, { notes }),
  reject: (id: number, notes: string) =>
    api.post(`/admin/tax-documents/${id}/reject/`, { notes }),
  download: (id: number) => api.get(`/admin/tax-documents/${id}/download/`),
};

// Production Portal API
export const productionAPI = {
  // Schedule and overview
  getSchedule: (params?: { view?: string; start_date?: string; end_date?: string; status?: string }) =>
    api.get('/production/schedule/', { params }),
  getToday: () => api.get('/production/today/'),
  getUpcoming: () => api.get('/production/upcoming/'),
  getUnallocatedItems: (daysAhead?: number) =>
    api.get('/production/unallocated_items/', { params: { days_ahead: daysAhead } }),

  // Batch generation
  generateBatches: (data: { days_ahead?: number; dry_run?: boolean }) =>
    api.post('/production/generate_batches/', data),

  // Batch reordering
  reorderBatches: (batchIds: number[]) =>
    api.post('/production/reorder/', { batch_ids: batchIds }),

  // Individual batch operations
  getBatch: (id: number) => api.get(`/production/batches/${id}/`),
  startBatch: (id: number) => api.post(`/production/batches/${id}/start/`),
  // completeBatch removed — /production/batches/<id>/complete/ is retired
  // (410 Gone). Completion goes through the Mixing Workspace's
  // complete_mixing (disposition flow).
  updateBatchStatus: (id: number, status: string) =>
    api.patch(`/production/batches/${id}/update_status/`, { status }),
  updateBatchNotes: (id: number, notes: string) =>
    api.patch(`/production/batches/${id}/update_notes/`, { notes }),

  // Feed Bank
  getFeedBank: (params?: { destination?: string; formula_id?: number }) =>
    api.get('/production/feed-bank/', { params }),
  getFeedBankSummary: () => api.get('/production/feed-bank/summary/'),
  getFeedBankAvailable: () => api.get('/production/feed-bank/available/'),
  useFeedBank: (id: number, data: { quantity: number; notes?: string }) =>
    api.post(`/production/feed-bank/${id}/use/`, data),
  createFeedBankEntry: (data: { formula_id: number; feed_form: string; quantity_lbs: number; destination?: string; notes?: string }) =>
    api.post('/production/feed-bank/', data),

  // Batch Mixing - for mill crew to mix batches
  getBatchMixing: (id: number) => api.get(`/production/mixing/${id}/`),
  addIngredient: (batchId: number, data: { raw_material_id: number; quantity: number }) =>
    api.post(`/production/mixing/${batchId}/add_ingredient/`, data),
  addAllIngredients: (batchId: number, ingredients: { raw_material_id: number; quantity: number }[]) =>
    api.post(`/production/mixing/${batchId}/add_all_ingredients/`, { ingredients }),
  clearIngredients: (batchId: number) =>
    api.post(`/production/mixing/${batchId}/clear_ingredients/`),
  adjustBatchSize: (batchId: number, newWeight: number) =>
    api.post(`/production/mixing/${batchId}/adjust_batch_size/`, { new_weight: newWeight }),
  checkInventory: (batchId: number) =>
    api.post(`/production/mixing/${batchId}/check_inventory/`),
  completeMixing: (batchId: number, data: {
    excess_destination?: string;
    excess_distribution?: Array<{ type: string; lbs: number; store_product_id?: number; bin_id?: number }>;
    close_order_items?: number[];
    make_up?: Array<{ order_item_id: number; lbs: number; source_type: 'BYOC_BIN' | 'FEED_BANK'; bin_id?: number; feed_bank_id?: number }>;
    notes?: string;
    force?: boolean;
  }) =>
    api.post(`/production/mixing/${batchId}/complete_mixing/`, data),
  matchingStoreProducts: (batchId: number) =>
    api.get(`/production/mixing/${batchId}/matching_store_products/`),
  matchingByocBins: (batchId: number) =>
    api.get(`/production/mixing/${batchId}/matching_byoc_bins/`),
  matchingFeedBank: (batchId: number) =>
    api.get(`/production/mixing/${batchId}/matching_feed_bank/`),
};

// Batch Search/Traceability API
export const batchSearchAPI = {
  search: (params: {
    batch_number?: string;
    formula_id?: number;
    date_from?: string;
    date_to?: string;
    customer_id?: number;
    status?: string;
  }) => api.get('/production-batches/search/', { params }),

  getTraceability: (batchId: number) =>
    api.get(`/production-batches/${batchId}/traceability/`),

  getAll: () => api.get('/production-batches/'),
  getById: (id: number) => api.get(`/production-batches/${id}/`),
};

// Admin Reports API
export const adminReportsAPI = {
  // Dashboard summary with filters
  getDashboard: (params?: {
    date_from?: string;
    date_to?: string;
    customer_id?: number;
    formula_id?: number;
  }) => api.get('/admin/reports/dashboard/', { params }),

  // Monthly sales data with YoY comparison
  getMonthlySales: (params?: {
    date_from?: string;
    date_to?: string;
    customer_id?: number;
  }) => api.get('/admin/reports/monthly_sales/', { params }),

  // Top customers by revenue and volume
  getTopCustomers: (params?: {
    date_from?: string;
    date_to?: string;
    limit?: number;
  }) => api.get('/admin/reports/top_customers/', { params }),

  // Top products by volume and revenue
  getTopProducts: (params?: {
    date_from?: string;
    date_to?: string;
    limit?: number;
    customer_id?: number;
  }) => api.get('/admin/reports/top_products/', { params }),

  // Sales breakdown by animal type
  getAnimalBreakdown: (params?: {
    date_from?: string;
    date_to?: string;
    customer_id?: number;
  }) => api.get('/admin/reports/animal_breakdown/', { params }),

  // Detailed customer breakdown
  getCustomerBreakdown: (params?: {
    date_from?: string;
    date_to?: string;
    search?: string;
  }) => api.get('/admin/reports/customer_breakdown/', { params }),

  // Detailed product breakdown
  getProductBreakdown: (params?: {
    date_from?: string;
    date_to?: string;
    customer_id?: number;
    animal_type?: string;
    search?: string;
  }) => api.get('/admin/reports/product_breakdown/', { params }),

  // Demand forecasting and predictions
  getForecasting: () => api.get('/admin/reports/forecasting/'),

  // Customer order history
  getCustomerHistory: (customerId: number) =>
    api.get('/admin/reports/customer_history/', { params: { customer_id: customerId } }),

  // Search orders by order number
  searchOrders: (query: string) =>
    api.get('/admin/reports/search_orders/', { params: { q: query } }),

  // Export CSV
  exportCSV: (params: {
    // caldwell_* is one file per section: readiness (or a single readiness
    // block: readiness_registers, _a, _b, _c, _e, _f, _step_zero),
    // restatement, timing, basket, growth. Each carries the caveats and
    // price-source counts in its header lines, so a section opened alone
    // still says what it rests on.
    type: 'orders' | 'customers' | 'products' | 'member_purchases'
      | `caldwell_${string}`;
    date_from?: string;
    date_to?: string;
    customer_id?: number;
    // Member export only: the file must match the population on screen, so
    // the search narrows the export the same way it narrows the table.
    search?: string;
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('type', params.type);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    if (params.customer_id) queryParams.append('customer_id', params.customer_id.toString());
    if (params.search) queryParams.append('search', params.search);
    return `${API_BASE_URL}/admin/reports/export_csv/?${queryParams.toString()}`;
  },

  // Member purchase history — who buys, how often, how much.
  // Dated by COMPLETION to match the Store Ledger; the Overview tab dates POS
  // by start time, so boundary transactions can differ by one day.
  // Kettle Falls sales restated at Caldwell prices. A projection, not revenue:
  // the payload carries its own caveats and the count of lines priced from each
  // source, and the page shows both above the total.
  getCaldwellProjection: (params?: {
    date_from?: string;
    date_to?: string;
  }) => api.get('/admin/reports/caldwell-projection/', { params }),

  getMemberPurchaseSummary: (params?: {
    date_from?: string;
    date_to?: string;
    search?: string;
    sort?: string;
    dir?: 'asc' | 'desc';
  }) => api.get('/admin/reports/member_purchase_summary/', { params }),

  // Get filter options (customers, products, animal types)
  getFilterOptions: () => api.get('/admin/reports/filter_options/'),

  // QR Analytics - Bridge Club QR code placement performance
  getQRAnalytics: () => api.get('/admin/bridge-club/qr-analytics/'),

  // Multi-channel dashboard
  getAllChannelDashboard: (params?: { date_from?: string; date_to?: string; channel?: string }) =>
    api.get('/admin/reports/all_channel_dashboard/', { params }),

  // Tax summary
  getTaxSummary: (params?: { date_from?: string; date_to?: string }) =>
    api.get('/admin/reports/tax_summary/', { params }),

  // Tax exempt log
  getTaxExemptLog: (params?: { date_from?: string; date_to?: string }) =>
    api.get('/admin/reports/tax_exempt_log/', { params }),

  // Payment breakdown
  getPaymentBreakdown: (params?: { date_from?: string; date_to?: string }) =>
    api.get('/admin/reports/payment_breakdown/', { params }),

  // POS summary
  getPOSSummary: (params?: { date_from?: string; date_to?: string }) =>
    api.get('/admin/reports/pos_summary/', { params }),

  // Feed license report
  getFeedLicenseReport: (params?: { half?: string; year?: number }) =>
    api.get('/admin/reports/feed_license_report/', { params }),

  // Sales detail
  getSalesDetail: (params?: {
    date_from?: string; date_to?: string; channel?: string;
    payment_method?: string; tax_exempt?: string; search?: string;
  }) => api.get('/admin/reports/sales_detail/', { params }),

  // Category sales breakdown
  getCategorySales: (params?: { date_from?: string; date_to?: string; channel?: string }) =>
    api.get('/admin/reports/category_sales/', { params }),

  // Promo campaigns report
  getPromoCampaignsReport: (params?: { date_from?: string; date_to?: string }) =>
    api.get('/admin/reports/promo-campaigns/', { params }),

  // Shipping analysis report
  getShippingAnalysis: (params?: { date_from?: string; date_to?: string }) =>
    api.get('/admin/reports/shipping-analysis/', { params }),

  // Discount analysis report
  getDiscountAnalysis: (params?: { date_from?: string; date_to?: string }) =>
    api.get('/admin/reports/discount-analysis/', { params }),

  // Export helpers
  exportReportCSV: (endpoint: string, params: Record<string, string>) => {
    const queryParams = new URLSearchParams({ ...params, export: 'csv' });
    return `${API_BASE_URL}/admin/reports/${endpoint}/?${queryParams.toString()}`;
  },
};

// Discount Categories API
export const discountCategoriesAPI = {
  list: () => api.get('/discount-categories/'),
  create: (data: { name: string; description?: string }) => api.post('/discount-categories/', data),
  update: (id: number, data: { name?: string; description?: string; is_active?: boolean }) =>
    api.patch(`/discount-categories/${id}/`, data),
};

// Security Dashboard API
export const securityAPI = {
  // Main dashboard
  getDashboard: () => api.get('/admin/security/dashboard/'),

  // Login attempts
  getLoginAttempts: (params?: {
    success?: boolean;
    ip_address?: string;
    email?: string;
    days?: number;
  }) => api.get('/admin/security/login-attempts/', { params }),

  // Top failed IPs and targeted accounts
  getTopFailedIPs: (params?: { days?: number; limit?: number }) =>
    api.get('/admin/security/top-failed-ips/', { params }),
  getTopTargetedAccounts: (params?: { days?: number; limit?: number }) =>
    api.get('/admin/security/top-targeted-accounts/', { params }),

  // Attack detection
  detectAttacks: () => api.get('/admin/security/detect-attacks/'),

  // IP blocking
  getBlockedIPs: () => api.get('/admin/security/blocked-ips/'),
  blockIP: (data: { ip_address: string; block_type: string; reason: string; duration_hours?: number }) =>
    api.post('/admin/security/block-ip/', data),
  unblockIP: (ip: string) => api.post(`/admin/security/unblock-ip/${ip}/`),

  // Account actions
  forcePasswordReset: (data: { user_id: number; reason: string }) =>
    api.post('/admin/security/force-password-reset/', data),
  disableAccount: (data: { user_id: number; reason: string }) =>
    api.post('/admin/security/disable-account/', data),
  enableAccount: (data: { user_id: number }) =>
    api.post('/admin/security/enable-account/', data),
  unlockAccount: (data: { user_id: number }) =>
    api.post('/admin/security/unlock-account/', data),

  // Lockouts
  getActiveLockouts: () => api.get('/admin/security/active-lockouts/'),

  // Audit log
  getAuditLog: (params?: {
    action?: string;
    user_id?: number;
    days?: number;
    limit?: number;
  }) => api.get('/admin/security/audit-log/', { params }),
  getAuditActionTypes: () => api.get('/admin/security/audit-action-types/'),

  // Security audits (from GitHub Actions)
  getSecurityAudits: (params?: { audit_type?: string; status?: string; limit?: number }) =>
    api.get('/admin/security/audits/', { params }),
  getSecurityAuditDetail: (id: number) => api.get(`/admin/security/audits/${id}/`),

  // Incidents
  getIncidents: (params?: { status?: string; severity?: string }) =>
    api.get('/admin/security/incidents/', { params }),
  createIncident: (data: {
    incident_type: string;
    severity: string;
    description: string;
    source_ip?: string;
    target_email?: string;
  }) => api.post('/admin/security/incidents/', data),
  updateIncidentStatus: (id: number, data: { status: string; notes?: string }) =>
    api.post(`/admin/security/incidents/${id}/status/`, data),
};

// Support Tickets Admin API
export const supportAdminAPI = {
  getTickets: (params?: { status?: string; priority?: string; category?: string; search?: string }) =>
    api.get('/support/admin/tickets/', { params }),
  getTicket: (id: number) => api.get(`/support/admin/tickets/${id}/`),
  updateTicket: (id: number, data: Record<string, any>) =>
    api.patch(`/support/admin/tickets/${id}/`, data),
  addMessage: (id: number, data: { message: string; is_internal?: boolean }) =>
    api.post(`/support/admin/tickets/${id}/messages/`, data),
  getStats: () => api.get('/support/admin/stats/'),
};

export default api;
